import CancelIcon from "@mui/icons-material/Cancel";
import FeedbackIcon from "@mui/icons-material/Feedback";
import SendIcon from "@mui/icons-material/Send";
import { Chip, Fab, Grid } from "@mui/material";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import Confetti from "react-confetti";
import { useTranslation } from "react-i18next";
import { type HospitalDataContextType, useHospitalData } from "../../contexts/HospitalDataProvider";
import { useWeekIndex } from "../../contexts/WeekIndexProvider";
import { INK, LIVE_DOT, STATUS } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";
import type { Day, Feedback, FeedbackValue, Shift } from "../../utils/models";
import axiosInstance from "../../utils/networkWrapper";
import { AreaSplitRow } from "../chart/AreaSplitRow";
import LineChart from "../chart/LineChart";
import type { SidebarAreaSplits } from "../chart/recommendationBreakdown";
import { ChartAdmDis } from "../chartAdmDis";
import { ChartCensus } from "../chartCensus";
import { ChartDateArea } from "../chartDateArea";
import { ChartScoreArea } from "../chartScoreArea";
import { DateSlider } from "../dateSlider";
import { PatientDrawer } from "../patientDrawer";
import MoonIcon from "../staffing/moon-icon.svg";
import SunIcon from "../staffing/sun-icon.svg";
import Style from "./ChartContainer.module.scss";

const useMobile = () => {
	const [windowWidth, setWindowWidth] = useState(window.innerWidth);
	const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

	useEffect(() => {
		// Handler to call on window resize
		const handleResize = () => {
			// Update window width
			setWindowWidth(window.innerWidth);
			// Update isMobile state
			setIsMobile(window.innerWidth < 768);
		};

		// Add event listener
		window.addEventListener("resize", handleResize);

		// Call handler right away so state gets updated with initial window size
		handleResize();

		// Remove event listener on cleanup
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []); // Empty array ensures effect is only run on mount and unmount

	return { isMobile, windowWidth };
};

export const Dot = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 49 50" fill="none">
		<g filter="url(#filter0_f_355_876)">
			<circle cx="24.5" cy="25" r="10.5" fill={LIVE_DOT.GLOW} />
		</g>
		<g filter="url(#filter1_di_355_876)">
			<circle cx="24.5" cy="25" r="10.5" fill={LIVE_DOT.CORE} />
			<circle cx="24.5" cy="25" r="9.1875" stroke={LIVE_DOT.RING} strokeWidth="2.625" />
		</g>
		<defs>
			<filter id="filter0_f_355_876" x="0" y="0.5" width="49" height="49" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
				<feFlood floodOpacity="0" result="BackgroundImageFix" />
				<feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
				<feGaussianBlur stdDeviation="5" result="effect1_foregroundBlur_355_876" />
			</filter>
			<filter id="filter1_di_355_876" x="6.125" y="10.125" width="36.75" height="36.75" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
				<feFlood floodOpacity="0" result="BackgroundImageFix" />
				<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
				<feOffset dy="1" />
				<feGaussianBlur stdDeviation="2" />
				<feComposite in2="hardAlpha" operator="out" />
				<feColorMatrix type="matrix" values="0 0 0 0 0.182291 0 0 0 0 0.732899 0 0 0 0 0.468607 0 0 0 0.2 0" />
				<feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_355_876" />
				<feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_355_876" result="shape" />
				<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
				<feOffset dy="1" />
				<feGaussianBlur stdDeviation="1.5" />
				<feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
				<feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
				<feBlend mode="normal" in2="shape" result="effect2_innerShadow_355_876" />
			</filter>
		</defs>
	</svg>
);

export const ChartContainer = () => {
	const { isMobile, windowWidth } = useMobile();
	const { t, i18n } = useTranslation();
	// Redesign shows only on hospital demos (Hospital type + inhouse2).
	const {
		chosenDepartment,
		getWeekIndexByDateTime,
		hospitalData,
		isHospitalDemo,
		isHospitalView: isHospitalDemoView,
		showErrorSnackbar,
	}: HospitalDataContextType = useHospitalData();
	const { weekIndex } = useWeekIndex();
	const [dateWeekDayLabel, setDateWeekDayLabel] = useState<DateTime>(DateTime.now());
	const [dailyCensusLow, setDailyCensusLow] = useState(0);
	const [dailyCensusHigh, setDailyCensusHigh] = useState(0);
	const [score, setScore] = useState(0);
	const [shiftNo, setShiftNo] = useState(0);
	const [censusColor, setCensusColor] = useState("");
	const [scoreColor, setScoreColor] = useState("");
	const [admissionsColor, setAdmissionsColor] = useState("");
	const [dischargesColor, setDischargesColor] = useState("");
	const [admissionsLow, setAdmissionsLow] = useState(0);
	const [admissionsHigh, setAdmissionsHigh] = useState(0);
	const [dischargesLow, setDischargesLow] = useState(0);
	const [dischargesHigh, setDischargesHigh] = useState(0);
	const [intermediateLow, setIntermediateLow] = useState(0);
	const [intermediateHigh, setntermediateHigh] = useState(0);
	const [areaSplits, setAreaSplits] = useState<SidebarAreaSplits | null>(null);
	const [rnRecommendation, setRnRecommendation] = useState(0);
	const [numOfAlert, setNumOfAlert] = useState(0);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [clickedShiftNo, setClickedShiftNo] = useState(false);
	const [clickedDayNo, setClickedDayNo] = useState(false);
	const [showFeedback, setShowFeedback] = useState(false);
	const [shouldFeedback, setShouldFeedback] = useState<boolean>(false);
	// Restored on feedback exit so we don't jump back to week 0.
	const [weekBeforeFeedback, setWeekBeforeFeedback] = useState(0);

	const [feedbackDate, setFeedbackDate] = useState<any>();
	const { setWeekIndex } = useWeekIndex();
	const [showConfetti, setShowConfetti] = useState(false);
	const [feedbackVals, setFeedbackVals] = useState<FeedbackValue[][]>([]);
	const get_day_index = (index: number) => {
		return i18n.dir() === "rtl" ? hospitalData.weeks[weekIndex].weeklyShifts[shiftNo].days.length - 1 - index : index;
	};
	const handleSendFeedbacks = () => {
		const formDataList = feedbackVals.flatMap((shiftRow, i) =>
			shiftRow.map((alignment, j) => {
				const formData = {
					shift_date: hospitalData?.weeks?.[weekIndex]?.weeklyShifts[i].days[get_day_index(j)].date?.toFormat("yyyy-MM-dd"),
					shift_type: hospitalData?.weeks?.[weekIndex]?.weeklyShifts[i].type,
					alignment: alignment,
					comment: "",
					feedback_type: "weekly",
				};

				trackEvent("feedback send", {
					...formData,
					department_id: chosenDepartment.department_id,
					first_day_date: hospitalData.weeks[weekIndex].weeklyShifts[0].days[0].date,
				});
				return formData;
			}),
		);

		axiosInstance
			.post(`/departments/feedbacks?department_id=${chosenDepartment.department_id}`, formDataList)
			.then(() => {
				setShowConfetti(true);
				setShouldFeedback(false);
				setShowFeedback(false);
				setTimeout(() => {
					setShowConfetti(false);
				}, 3000);
			})
			.catch((error) => {
				showErrorSnackbar(
					"failed to send feedback",
					{
						action: "failed to send feedback",
						department_id: chosenDepartment.department_id,
					},
					error,
				);
			});
	};

	useEffect(() => {
		if (!hospitalData?.weeks?.[0]?.weeklyShifts?.length) return;
		setFeedbackVals(
			Array(hospitalData.weeks[0].weeklyShifts.length)
				.fill(null)
				.map(() => Array(7).fill("Precisely")),
		);
	}, [hospitalData?.weeks?.[0]?.weeklyShifts?.length]);

	useEffect(() => {
		if (!hospitalData?.feedbacks?.length) {
			setShouldFeedback(false);
			return;
		}

		setShouldFeedback(hospitalData.feedbacks.some((currFeedback) => !currFeedback.filled));
		setFeedbackDate(hospitalData.feedbacks[0].shiftDate);
	}, [hospitalData.feedbacks]);

	useEffect(() => {
		let count = 0;
		hospitalData?.weeks[weekIndex]?.weeklyShifts.forEach((shift: any, i: number) => {
			hospitalData?.weeks[weekIndex]?.weeklyShifts[i].days.forEach((day: any) => {
				if (day.changeAlert) {
					count++;
				}
			});
		});
		setNumOfAlert(count);
	}, [weekIndex, shiftNo, hospitalData]);

	function printIntermediateRoom() {
		return (
			<>
				{chosenDepartment?.has_intermediate && (
					<ChartAdmDis
						dailyCensusTitle={t("Intermediate Room")}
						dailyCensusValue={intermediateHigh || intermediateLow ? `${intermediateLow}-${intermediateHigh}` : "0"}
						dailyCensusColor={censusColor ?? ""}
						isHospitalDemo={isHospitalDemoView}
					/>
				)}
			</>
		);
	}

	function findFirstUnfilledIndex(feedbackArray: Feedback[]): number {
		return feedbackArray.findIndex((feedback) => !feedback.filled);
	}

	// Function to find the last unfilled feedback index
	function findLastUnfilledIndex(feedbackArray: Feedback[]): number {
		for (let i = feedbackArray.length - 1; i >= 0; i--) {
			if (!feedbackArray[i].filled) {
				return i;
			}
		}
		return -1;
	}

	return (
		<div
			data-hospital-demo={isHospitalDemoView ? "true" : undefined}
			style={{
				width: "100%",
				height: "100%",
				position: "relative",
				paddingBottom: "10px",
			}}
		>
			<Grid container sx={{ flexDirection: "row", flexWrap: "nowrap", height: "100%" }}>
				{!isMobile && (
					<Grid
						style={{
							width: "238px",
							paddingRight: "10px",
							paddingLeft: "10px",
							height: "100%",
						}}
					>
						<div style={{ width: "100%", height: "100%" }}>
							<Grid
								container
								sx={{
									height: "100%",
									maxHeight: "calc(100vh - 130px)",
									flexDirection: "column",
									justifyContent: "space-between",
									alignItems: "flex-start",
									flexWrap: "nowrap",
								}}
							>
								<div />
								{/* The rail gained a per-area line under each metric plus an RN Recommendation
								    section, so on a short viewport its content is taller than the Grid's
								    maxHeight. minHeight:0 lets this flex child shrink below its content height
								    (flex items default to min-height:auto), so overflowY can actually scroll
								    instead of the content spilling past the bottom of the page. */}
								<div style={{ overflowY: "auto", minHeight: 0 }}>
									<ChartDateArea
										day={dateWeekDayLabel}
										shiftNo={shiftNo}
										isHospitalDemo={isHospitalDemoView}
										legend={areaSplits && <AreaSplitRow parts={areaSplits.census} asLegend />}
									/>
									{/* Hospital-demo depts use per-section separators (SCSS) instead of these two
									    full-width <hr>s, so the <hr>s only render for regular depts (as on main). */}
									{!isHospitalDemoView && (
										<div
											style={{
												display: "flex",
												width: "100%",
												justifyContent: "flex-start",
											}}
										>
											<hr
												style={{
													width: "calc(100% - 50px)",
													borderBottom: "2px solid var(--inhouse-neutral)",
													marginInlineStart: "0",
													marginInlineEnd: "auto",
												}}
											/>
										</div>
									)}

									<ChartCensus
										dailyCensusTitle={t("Daily Census")}
										dailyCensusValue={`${dailyCensusHigh ? `${dailyCensusLow}-${dailyCensusHigh}` : `${dailyCensusLow}`}`}
										dailyCensusColor={censusColor ?? ""}
										isHospitalDemo={isHospitalDemoView}
										breakdown={areaSplits && <AreaSplitRow parts={areaSplits.census} />}
									/>
									<ChartAdmDis
										dailyCensusTitle={t("Admissions")}
										dailyCensusValue={admissionsHigh || admissionsLow ? `${admissionsLow}-${admissionsHigh}` : "0"}
										dailyCensusColor={admissionsColor}
										isHospitalDemo={isHospitalDemoView}
										breakdown={areaSplits && <AreaSplitRow parts={areaSplits.admissions} />}
									/>
									<ChartAdmDis
										dailyCensusTitle={t("Discharges")}
										dailyCensusValue={dischargesHigh || dischargesLow ? `${dischargesLow}-${dischargesHigh}` : "0"}
										dailyCensusColor={dischargesColor}
										isHospitalDemo={isHospitalDemoView}
										breakdown={areaSplits && <AreaSplitRow parts={areaSplits.discharges} />}
									/>

									{printIntermediateRoom()}
									{!isHospitalDemoView && (
										<div
											style={{
												display: "flex",
												width: "100%",
												justifyContent: "flex-start",
											}}
										>
											<hr
												style={{
													width: "calc(100% - 50px)",
													borderBottom: "2px solid var(--inhouse-neutral)",
													marginInlineStart: "0",
													marginInlineEnd: "auto",
												}}
											/>
										</div>
									)}
									<ChartScoreArea
										patientScore={`${score}`}
										patientScoreText={t("Patient Score")}
										scoreColor={scoreColor ?? ""}
										breakdown={areaSplits && <AreaSplitRow parts={areaSplits.score} />}
									/>
									{areaSplits && (
										<ChartCensus
											dailyCensusTitle={t("shift.insights.rn.recommendation")}
											dailyCensusValue={`${rnRecommendation}`}
											dailyCensusColor={INK.PRIMARY}
											isHospitalDemo={isHospitalDemoView}
											breakdown={<AreaSplitRow parts={areaSplits.nurses} />}
										/>
									)}
								</div>
								<div />
							</Grid>
						</div>
					</Grid>
				)}
				<Grid style={{ flexGrow: 1 }}>
					<div style={{ width: "100%", height: "calc(100% - 60px)" }}>
						{" "}
						{isMobile ? (
							<Grid
								container
								sx={{
									width: "100vw",
									position: "fixed",
									flexDirection: "row",
									justifyContent: "space-evenly",
									alignItems: "center",
								}}
							>
								<div style={{ paddingTop: "15px" }}>
									<DateSlider hideArrows={showFeedback} />
								</div>
								{/* DEFAULT: hospital demos always show the "new updates" chip (they have no live alert data),
								    falling back to a count of 1 below; real departments stay data-driven on numOfAlert. */}
								{numOfAlert > 0 || isHospitalDemoView ? (
									<Chip
										style={{ margin: 0, marginRight: 15, marginLeft: 15 }}
										label={
											<Grid
												container
												sx={{
													flexDirection: "row",
													justifyContent: "flex-start",
													alignItems: "center",
												}}
											>
												<Dot />{" "}
												<div className={isHospitalDemoView ? `${Style.dateFont} ${Style.dateFontDemo}` : Style.dateFont}>
													{" "}
													{numOfAlert || 1} {t("new.updates.mobile")}
												</div>
											</Grid>
										}
										variant="outlined"
									/>
								) : (
									<div style={{ width: "150px" }} />
								)}
							</Grid>
						) : (
							<Grid
								container
								sx={{
									flexDirection: "row",
									justifyContent: "space-between",
									alignItems: "center",
								}}
							>
								<div style={{ height: "1px", width: "215" }} />
								<div style={{ paddingTop: "15px" }}>
									<DateSlider hideArrows={showFeedback} />
								</div>
								{/* DEFAULT: hospital demos always show the "new updates" chip (they have no live alert data),
								    falling back to a count of 1 below; real departments stay data-driven on numOfAlert. */}
								{numOfAlert > 0 || isHospitalDemoView ? (
									<Chip
										style={{ margin: 0, marginRight: 15 }}
										label={
											<Grid
												container
												sx={{
													flexDirection: "row",
													justifyContent: "flex-start",
													alignItems: "center",
												}}
											>
												<Dot />{" "}
												<div className={isHospitalDemoView ? `${Style.dateFont} ${Style.dateFontDemo}` : Style.dateFont}>
													{" "}
													{numOfAlert || 1} {t("new.updates")}
												</div>
											</Grid>
										}
										variant="outlined"
									/>
								) : (
									<div style={{ width: "240px" }} />
								)}
							</Grid>
						)}
						{/* Header track mirrors the chart boxes' geometry (same width + per-box
						    left/width %), so each header sits centered over its box instead of
						    drifting right across the week. */}
						{(() => {
							const dayDates = hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo].days
								.map((d: Day) => d.date)
								.sort((a: DateTime, b: DateTime) => a.valueOf() - b.valueOf());
							const dayCount = dayDates?.length ?? 0;
							return (
								<div
									style={{
										position: "relative",
										height: "50px",
										maxWidth: "100%",
										minWidth: isMobile ? "200vw" : "calc(36vw - 2px)",
										paddingTop: isMobile ? 50 : 10,
										paddingLeft: isMobile ? 0 : 20,
										paddingRight: isMobile ? 0 : 10,
									}}
								>
									<div style={{ position: "relative", width: "100%", maxWidth: "calc(95vw - 245px)", minWidth: isMobile ? "200vw" : "calc(36vw - 2px)" }}>
										{dayDates?.map((m: DateTime, index: number) => (
											<div
												key={index}
												className={isHospitalDemoView ? `${Style.dateFont} ${Style.dateFontDemo}` : Style.dateFont}
												style={{ position: "absolute", left: `${(index * 100) / dayCount}%`, width: `${100 / dayCount}%`, textAlign: "center" }}
											>
												<div style={{ color: m.hasSame(DateTime.now(), "day") ? "var(--inhouse-primary)" : "inherit" }}>
													{(m.toFormat("ccc") as string).toUpperCase()}
												</div>

												{m.hasSame(DateTime.now(), "day") ? (
													<div
														style={{
															color: STATUS.INFO.contrastText,
															width: "24px",
															height: "24px",
															borderRadius: "50%",
															backgroundColor: "var(--inhouse-primary)",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															margin: "auto",
														}}
													>
														{m.toFormat("d")}
													</div>
												) : (
													<div style={{ color: INK.PRIMARY }}>{m.toFormat("d")}</div>
												)}
											</div>
										))}
									</div>
								</div>
							);
						})()}
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								height: "100%",
								minHeight: "12vh",
								overflowY: "auto",
								flexGrow: 1,
								paddingBottom: 25,
								paddingLeft: 20,
								paddingRight: 10,
							}}
						>
							<PatientDrawer
								drawerOpen={drawerOpen}
								setDrawerOpen={setDrawerOpen}
								setClickedShiftNo={setClickedShiftNo}
								clickedShiftNo={clickedShiftNo}
								setClickedDayNo={setClickedDayNo}
								clickedDayNo={clickedDayNo}
								weekIndex={weekIndex}
							/>

							{hospitalData?.weeks[weekIndex]?.weeklyShifts?.map((shift: Shift, index: number) => (
								<div className={Style.chartContent} key={index}>
									<div
										style={{
											position: "relative",
											height: 0,
											top: "62%",
											right: i18n.dir() === "rtl" ? "-23px" : "25px",
										}}
									>
										<div
											className={isHospitalDemoView ? `${Style.dateFont} ${Style.dateFontDemo}` : Style.dateFont}
											style={{
												transform: "rotate(270deg)",
												transformOrigin: "left top",
												zIndex: 99999,
												width: "90px",
												// The sun/moon icon + flex-row layout is a hospital-demo-only treatment;
												// regular depts keep the original text-only rotated label.
												...(isHospitalDemoView && {
													display: "flex",
													flexDirection: "row",
													alignItems: "center",
													justifyContent: "center",
													gap: "4px",
													whiteSpace: "nowrap",
												}),
											}}
										>
											{isHospitalDemoView
												? // Hospital demo relabels the Day shift as "Morning" and shows sun/moon icons + AM/PM.
													(() => {
														const isDayShift = shift.type === "Morning" || shift.type === "Day";
														return (
															<>
																<span style={{ flexShrink: 0, display: "inline-flex", width: 15, height: 15 }}>
																	{isDayShift ? <SunIcon aria-hidden="true" /> : <MoonIcon aria-hidden="true" />}
																</span>
																<span>{isDayShift ? `${t("Morning")} (AM)` : `${t("Night")} (PM)`}</span>
															</>
														);
													})()
												: // Preserve main's behavior for everyone else: the legacy InHouse__Hospital_Demo mock
													// (isHospitalDemo) still gets the (AM)/(PM) suffix; truly regular depts get none.
													t(hospitalData?.weeks[weekIndex]?.weeklyShifts[index % 3].type) +
													(!isHospitalDemo ? "" : shift.type === "Morning" || shift.type === "Day" ? " (AM)" : " (PM)")}
										</div>
									</div>
									<div id={`${windowWidth}.`} className={Style.chartContent} key={index}>
										<LineChart
											showFeedback={showFeedback}
											setDateWeekDayLabel={setDateWeekDayLabel}
											setDailyCensusLow={setDailyCensusLow}
											setDailyCensusHigh={setDailyCensusHigh}
											setScore={setScore}
											setCensusColor={setCensusColor}
											setScoreColor={setScoreColor}
											setAdmissionsColor={setAdmissionsColor}
											setDischargesColor={setDischargesColor}
											shiftNo={index}
											setShiftNo={setShiftNo}
											setAdmissionsLow={setAdmissionsLow}
											setAdmissionsHigh={setAdmissionsHigh}
											setDischargesLow={setDischargesLow}
											setDischargesHigh={setDischargesHigh}
											setIntermediateLow={setIntermediateLow}
											setIntermediateHigh={setntermediateHigh}
											drawerOpen={drawerOpen}
											setDrawerOpen={setDrawerOpen}
											setClickedShiftNo={setClickedShiftNo}
											setClickedDayNo={setClickedDayNo}
											feedbackVals={feedbackVals}
											setFeedbackVals={setFeedbackVals}
											setAreaSplits={setAreaSplits}
											setRnRecommendation={setRnRecommendation}
										/>
									</div>
								</div>
							))}
						</div>
					</div>
				</Grid>
			</Grid>
			{shouldFeedback && !isMobile && (
				<Fab
					onClick={() => {
						if (!showFeedback) {
							setWeekBeforeFeedback(weekIndex);
							setShowFeedback(true);
							// find weekIndex from feedbackDate
							setWeekIndex(getWeekIndexByDateTime(feedbackDate));
						} else {
							handleSendFeedbacks();
						}
					}}
					sx={
						i18n.dir() === "rtl"
							? {
									position: "absolute",
									bottom: "10px",
									right: "90px",
									background: "primary.light",
								}
							: {
									position: "absolute",
									bottom: "10px",
									left: "90px",
									background: "primary.light",
								}
					}
					variant="extended"
				>
					{showFeedback ? (
						<>
							<>
								{t("Send")}
								<SendIcon sx={{ mr: 1 }} />
							</>
						</>
					) : (
						<>
							{i18n.dir() === "rtl" ? (
								<>
									{t("Feedback Time")}
									<FeedbackIcon sx={{ mr: 1 }} />
								</>
							) : (
								<>
									<FeedbackIcon sx={{ mr: 1 }} />
									{t("Feedback Time")}
								</>
							)}
						</>
					)}
				</Fab>
			)}
			{showFeedback && (
				<Fab
					onClick={() => {
						setShowFeedback(false);
						setWeekIndex(weekBeforeFeedback); // Return to the week the user came from
					}}
					sx={i18n.dir() === "rtl" ? { position: "absolute", bottom: "10px", right: "190px" } : { position: "absolute", bottom: "10px", left: "190px" }}
					size="small"
				>
					<CancelIcon />
				</Fab>
			)}
			{showConfetti && <Confetti width={window.innerWidth} height={window.innerHeight} recycle={false} numberOfPieces={200} gravity={0.3} />}
		</div>
	);
};
